import type { BookingData, BookingDurationMinutes } from '@/types/booking'
import { clinicalTeam } from '@/lib/clinicalteam'
import { drMeena } from '@/lib/drMeena'

export type BookingDoctorRole = 'Psychiatrist' | 'Psychologist'

export type BookingDoctorOption = {
  id: string
  name: string
  specialty: string
  image: string
  languagesKnown: readonly string[]
  badge?: string
  role: BookingDoctorRole
  aliases: readonly string[]
  allowedDurations: readonly BookingDurationMinutes[]
  defaultDuration: BookingDurationMinutes
}

const normalizeDoctorSearchText = (value: string | null | undefined) =>
  String(value ?? '')
    .normalize('NFKD')
    .toLowerCase()
    .replace(/^dr\.?\s+/i, '')
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim()

const buildAliases = (...values: Array<string | null | undefined>) =>
  values
    .map(normalizeDoctorSearchText)
    .filter((value): value is string => value.length > 0)

export const bookingDoctors: BookingDoctorOption[] = [
  {
    id: String(drMeena.id),
    name: `Dr. ${drMeena.name}`,
    specialty: drMeena.specialty,
    image: drMeena.imageUrl,
    languagesKnown: drMeena.languagesKnown ?? [],
    badge: 'CEO & Medical Director',
    role: 'Psychiatrist',
    allowedDurations: [15, 30, 60],
    defaultDuration: 15,
    aliases: buildAliases(
      drMeena.name,
      `Dr. ${drMeena.name}`,
      'Meena',
      'Dr Meena',
      'Meena Gnanashekharan',
      'Meena Gnanasekharan',
      'Dr. Meena Gnanashekharan',
      'Dr. Meena Gnanasekharan',
    ),
  },
  ...clinicalTeam.map((member) => ({
    id: String(member.id),
    name: member.name,
    specialty: member.specialty ?? member.title ?? 'Psychologist',
    image: member.imageUrl,
    languagesKnown: member.languagesKnown ?? [],
    role: 'Psychologist' as const,
    allowedDurations: [15, 30, 60] as const,
    defaultDuration: 60 as const,
    aliases: buildAliases(
      member.name,
      member.title,
      member.specialty,
      member.name.split(' ')[0],
    ),
  })),
]

export function getBookingDoctorById(
  doctorId: string | null | undefined,
): BookingDoctorOption | null {
  const id = String(doctorId ?? '').trim()
  if (!id) return null

  return bookingDoctors.find((doctor) => doctor.id === id) ?? null
}

export function resolvePreferredDoctor(
  preferredPsychologist: string | null | undefined,
): BookingDoctorOption | null {
  const preferred = normalizeDoctorSearchText(preferredPsychologist)

  if (!preferred) return null

  return (
    bookingDoctors.find((doctor) => {
      const doctorName = normalizeDoctorSearchText(doctor.name)
      const specialty = normalizeDoctorSearchText(doctor.specialty)

      if (doctor.aliases.includes(preferred)) return true
      if (doctorName === preferred) return true
      if (doctorName.includes(preferred)) return true
      if (preferred.includes(doctorName)) return true
      if (specialty === preferred) return true

      return doctor.aliases.some(
        (alias) =>
          alias === preferred ||
          alias.includes(preferred) ||
          preferred.includes(alias),
      )
    }) ?? null
  )
}

export function applyDoctorToBookingData(
  bookingData: BookingData,
  doctor: BookingDoctorOption,
): BookingData {
  return {
    ...bookingData,
    doctorID: doctor.id,
    doctorName: doctor.name,
    doctorSpecialty: doctor.specialty,
    durationMinutes: doctor.defaultDuration,
    bookingDate: undefined,
    slot: '',
  }
}
